import type { CVSensitiveInfo } from '@cibli/shared';

import { useCallback, useRef } from 'react';

import type { ClassicParams } from './schema';

import CVContainer from '../../components/CVContainer';
import { usePageDelimitation } from '../../hooks/usePageDelimitation';
import { usePdfPrintReady } from '../../hooks/usePdfPrintReady';
import { getHeadingColor } from '../helpers';
import { TemplateDisplayProps } from '../types';
import ComplementaryInfo from './components/ComplementaryInfo';
import Education from './components/Education';
import Experience from './components/Experience';
import HobbiesAndInterests from './components/HobbiesAndInterests';
import Languages from './components/Languages';
import PersonalExperiences from './components/PersonalExperiences';
import PersonalInfo from './components/PersonalInfo';
import Skills from './components/Skills';
import Summary from './components/Summary';

/** Classic: 2 columns, 5 sections per column (left: PersonalInfo, Skills, Languages, Hobbies, ComplementaryInfo; right: Summary, Experience, PersonalExperiences, Education). */
const SECTION_COUNTS = [5, 4] as const;
const SPLITABLE_SECTIONS: [number, number][] = [
  [1, 1],
  [1, 2],
  [1, 3],
];

export default function Display({
  cv,
  params,
  onEdit,
  mode = 'preview',
}: TemplateDisplayProps<ClassicParams>) {
  const sensitiveInfo: CVSensitiveInfo = {
    firstName: cv.firstName,
    lastName: cv.lastName,
    phone: cv.phone,
    email: cv.email,
  };

  const headingColor = getHeadingColor(params.accentColor);

  const containerRef = useRef<HTMLDivElement>(null);
  const sectionRefsRef = useRef<(HTMLElement | null)[][]>([]);

  const setBlockRef = useCallback((col: number, blockIndex: number) => {
    return (el: HTMLElement | null) => {
      if (!sectionRefsRef.current[col]) sectionRefsRef.current[col] = [];
      sectionRefsRef.current[col][blockIndex] = el;
    };
  }, []);

  const contentKey = [
    cv.competences?.length ?? 0,
    cv.experiences?.length ?? 0,
    cv.education?.length ?? 0,
    cv.personalExperiences?.length ?? 0,
    cv.summary?.length ?? 0,
    cv.hasRQTH ? 1 : 0,
    cv.complementaryMentions?.length ?? 0,
  ].join('-');

  const { blockOffsets, splitLayout, pageCount, pageHeightPx, isLayoutReady } = usePageDelimitation(
    sectionRefsRef,
    containerRef,
    {
      contentKey,
      bodyBottomPaddingPx: 80,
      splitableSections: SPLITABLE_SECTIONS,
      sectionCountPerColumn: SECTION_COUNTS as unknown as number[],
    },
  );

  usePdfPrintReady(mode === 'print', containerRef, isLayoutReady);

  const containerHeight = pageCount > 0 && pageHeightPx > 0 ? pageCount * pageHeightPx : undefined;

  const renderRightColumnBlocks = () => {
    const col = 1;
    const offsets = blockOffsets[col] ?? [];
    const layout = splitLayout[col] ?? [];
    let blockIndex = 0;
    const nodes: React.ReactNode[] = [];

    nodes.push(
      <div
        key="0"
        ref={setBlockRef(col, blockIndex)}
        style={{ marginTop: offsets[blockIndex] ?? 0 }}
      >
        <Summary
          summary={cv.summary}
          onEdit={onEdit ? () => onEdit('summary') : undefined}
          headingColor={headingColor}
        />
      </div>,
    );
    blockIndex++;

    const expChunks = layout[1] ?? [
      { showTitle: true, startIndex: 0, endIndex: cv.experiences?.length ?? 0 },
    ];
    expChunks.forEach((chunk, ci) => {
      nodes.push(
        <div
          key={`1-${ci}`}
          ref={setBlockRef(col, blockIndex)}
          style={{ marginTop: offsets[blockIndex] ?? 0 }}
        >
          <Experience
            experiences={cv.experiences}
            onEdit={onEdit ? () => onEdit('experiences') : undefined}
            chunk={chunk}
            headingColor={headingColor}
          />
        </div>,
      );
      blockIndex++;
    });

    const peChunks = layout[2] ?? [
      {
        showTitle: true,
        startIndex: 0,
        endIndex: cv.personalExperiences?.length ?? 0,
      },
    ];
    peChunks.forEach((chunk, ci) => {
      nodes.push(
        <div
          key={`2-${ci}`}
          ref={setBlockRef(col, blockIndex)}
          style={{ marginTop: offsets[blockIndex] ?? 0 }}
        >
          <PersonalExperiences
            personalExperiences={cv.personalExperiences}
            onEdit={onEdit ? () => onEdit('personalExperiences') : undefined}
            chunk={chunk}
            headingColor={headingColor}
          />
        </div>,
      );
      blockIndex++;
    });

    const eduChunks = layout[3] ?? [
      { showTitle: true, startIndex: 0, endIndex: cv.education?.length ?? 0 },
    ];
    eduChunks.forEach((chunk, ci) => {
      nodes.push(
        <div
          key={`3-${ci}`}
          ref={setBlockRef(col, blockIndex)}
          style={{ marginTop: offsets[blockIndex] ?? 0 }}
        >
          <Education
            education={cv.education}
            onEdit={onEdit ? () => onEdit('education') : undefined}
            chunk={chunk}
            headingColor={headingColor}
          />
        </div>,
      );
      blockIndex++;
    });

    return nodes;
  };

  return (
    <CVContainer accentColor={params.accentColor} mode={mode}>
      <div
        ref={containerRef}
        data-page-count={pageCount}
        className="relative"
        style={containerHeight != null ? { height: containerHeight } : undefined}
      >
        <div className="h-full flex flex-col">
          <div className="pt-5">
            <main className="grid grid-cols-3 gap-0 flex-1 min-h-0">
              {/* Left column (col 0): PersonalInfo, Skills, Languages, HobbiesAndInterests */}
              <section className="bg-gray-100 px-3 pb-20 flex flex-col gap-2 overflow-visible">
                <div ref={setBlockRef(0, 0)} style={{ marginTop: blockOffsets[0]?.[0] ?? 0 }}>
                  <PersonalInfo
                    personalInfos={cv}
                    sensitiveInfo={sensitiveInfo}
                    onEdit={onEdit ? () => onEdit('personalInfos') : undefined}
                    mode={mode}
                    headingColor={headingColor}
                  />
                </div>
                <div ref={setBlockRef(0, 1)} style={{ marginTop: blockOffsets[0]?.[1] ?? 0 }}>
                  <Skills
                    competences={cv.competences}
                    onEdit={onEdit ? () => onEdit('competences') : undefined}
                    headingColor={headingColor}
                  />
                </div>
                <div ref={setBlockRef(0, 2)} style={{ marginTop: blockOffsets[0]?.[2] ?? 0 }}>
                  <Languages
                    languages={cv.languages}
                    onEdit={onEdit ? () => onEdit('languages') : undefined}
                    headingColor={headingColor}
                  />
                </div>
                <div ref={setBlockRef(0, 3)} style={{ marginTop: blockOffsets[0]?.[3] ?? 0 }}>
                  <HobbiesAndInterests
                    hobbiesAndInterests={cv.hobbiesAndInterests}
                    onEdit={onEdit ? () => onEdit('hobbiesAndInterests') : undefined}
                    headingColor={headingColor}
                  />
                </div>
                <div ref={setBlockRef(0, 4)} style={{ marginTop: blockOffsets[0]?.[4] ?? 0 }}>
                  <ComplementaryInfo
                    hasRQTH={cv.hasRQTH}
                    complementaryMentions={cv.complementaryMentions}
                    onEdit={onEdit ? () => onEdit('complementaryInfo') : undefined}
                    headingColor={headingColor}
                  />
                </div>
              </section>
              {/* Right column (col 1): Summary, Experience, PersonalExperiences, Education */}
              <section className="col-span-2 px-4 pb-20 flex flex-col gap-2 overflow-visible">
                {renderRightColumnBlocks()}
              </section>
            </main>
          </div>
        </div>

        {/* Logo Cibli at bottom right of each page */}
        {Array.from({ length: pageCount }, (_, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 pointer-events-none"
            style={{ top: i * pageHeightPx, height: pageHeightPx }}
          >
            <div className="absolute bottom-2 right-2">
              <img src="/logo.svg" alt="Cibli" className="w-15" />
            </div>
          </div>
        ))}
      </div>
    </CVContainer>
  );
}
